import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePickerField } from "@/components/DatePickerField";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// ID-038 RC-CS-REV-001: structured revision — dealer edits the fields, reviews the changes, then confirms
const lbl = "text-xs font-semibold uppercase tracking-wider text-warmgrey";
const initial = (l, req) => ({
  color_codes: [...(l.color_codes || [])], quantity_sqft: String(l.quantity_sqft ?? ""), desired_delivery_date: req.desired_delivery_date || "",
  format_design: l.format_mode === "CUSTOM" ? l.custom_shape_desc || "" : l.format_in || "", dimensions: l.dimensions || "", application: l.application || "",
});

function useCatalogColors() {
  const [colors, setColors] = useState({});
  useEffect(() => {
    api.get("/catalog").then((r) => {
      const m = {};
      (r.data || []).forEach((v) => { if (v.color_code && !m[v.color_code]) m[v.color_code] = v.color_name || v.color_code; });
      setColors(m);
    }).catch(() => {});
  }, []);
  return colors;
}

function diff(a, b, names) {
  const nm = (codes) => codes.map((c) => `${c} ${names[c] || ""}`.trim()).join("; ");
  const rows = [];
  if (a.color_codes.join() !== b.color_codes.join()) rows.push(["Color", nm(a.color_codes), nm(b.color_codes)]);
  if (parseFloat(a.quantity_sqft) !== parseFloat(b.quantity_sqft)) rows.push(["Quantity", `${a.quantity_sqft} sq ft`, `${b.quantity_sqft} sq ft`]);
  if (a.desired_delivery_date !== b.desired_delivery_date) rows.push(["Desired delivery date", a.desired_delivery_date || "-", b.desired_delivery_date || "-"]);
  if (a.format_design.trim() !== b.format_design.trim()) rows.push(["Format / design", a.format_design || "-", b.format_design]);
  if (a.dimensions.trim() !== b.dimensions.trim()) rows.push(["Dimensions", a.dimensions || "-", b.dimensions]);
  if (a.application.trim() !== b.application.trim()) rows.push(["Application / location", a.application || "-", b.application]);
  return rows;
}

export function RevisionForm({ req, busy, onSubmit, onCancel }) {
  const names = useCatalogColors();
  const [lineIdx, setLineIdx] = useState(0);
  const line = (req.lines || [])[lineIdx] || {};
  const custom = line.format_mode === "CUSTOM";
  const [v, setV] = useState(() => initial(line, req));
  const [change, setChange] = useState("");
  const [notes, setNotes] = useState("");
  const [review, setReview] = useState(false);
  useEffect(() => { setV(initial((req.lines || [])[lineIdx] || {}, req)); setReview(false); }, [lineIdx, req]);
  const set = (k, val) => { setV((cur) => ({ ...cur, [k]: val })); setReview(false); };
  const rows = diff(initial(line, req), v, names);
  const submit = () => onSubmit({
    change, notes, confirmed: true,
    changes: { line_index: lineIdx, color_codes: v.color_codes, quantity_sqft: parseFloat(v.quantity_sqft), desired_delivery_date: v.desired_delivery_date || null,
      format_design: v.format_design, dimensions: custom ? v.dimensions : null, application: v.application },
  });
  const codes = Object.keys(names).sort();
  return (
    <div className="mt-6 rounded-md border border-border bg-white p-6" data-testid="custom-revision-form">
      <p className="text-sm font-medium text-charcoal">What should change for revision {String((req.spec_versions || []).length + 1).padStart(2, "0")}?</p>
      {(req.lines || []).length > 1 && (
        <Select value={String(lineIdx)} onValueChange={(x) => setLineIdx(parseInt(x, 10))}>
          <SelectTrigger className="mt-3 h-10 bg-white" data-testid="revision-line-select"><SelectValue /></SelectTrigger>
          <SelectContent>{req.lines.map((l, i) => <SelectItem key={i} value={String(i)}>Line {i + 1}: {l.summary}</SelectItem>)}</SelectContent>
        </Select>
      )}
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {v.color_codes.map((c, i) => (
          <div key={i}><Label className={lbl}>Color{v.color_codes.length > 1 ? ` ${i + 1}` : ""}</Label>
            <Select value={c} onValueChange={(x) => set("color_codes", v.color_codes.map((o, j) => (j === i ? x : o)))}>
              <SelectTrigger className="mt-1 h-10 bg-white" data-testid={`revision-color-${i}`}><SelectValue placeholder="Choose a color" /></SelectTrigger>
              <SelectContent className="max-h-72">{codes.map((k) => <SelectItem key={k} value={k} data-testid={`revision-color-option-${k}`}>{k} {names[k]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}
        <div><Label className={lbl}>Quantity (sq ft)</Label><Input type="number" min="0" value={v.quantity_sqft} onChange={(e) => set("quantity_sqft", e.target.value)} className="mt-1 h-10 bg-white" data-testid="revision-quantity" /></div>
        <div><Label className={lbl}>Desired delivery date</Label><div className="mt-1"><DatePickerField value={v.desired_delivery_date} onChange={(d) => set("desired_delivery_date", d)} className="h-10" testId="revision-delivery-date" /></div></div>
        {custom ? (
          <div><Label className={lbl}>Design / custom shape</Label><Input value={v.format_design} onChange={(e) => set("format_design", e.target.value)} className="mt-1 h-10 bg-white" data-testid="revision-format-design" /></div>
        ) : (
          <div><Label className={lbl}>Format</Label>
            <Select value={v.format_design} onValueChange={(x) => set("format_design", x)}>
              <SelectTrigger className="mt-1 h-10 bg-white" data-testid="revision-format-design"><SelectValue /></SelectTrigger>
              <SelectContent>{["4x4", "2x6", "2x2"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {custom && <div><Label className={lbl}>Dimensions</Label><Input value={v.dimensions} onChange={(e) => set("dimensions", e.target.value)} className="mt-1 h-10 bg-white" data-testid="revision-dimensions" /></div>}
        <div><Label className={lbl}>Application / location</Label><Input value={v.application} onChange={(e) => set("application", e.target.value)} className="mt-1 h-10 bg-white" data-testid="revision-application" /></div>
      </div>
      <Input value={change} onChange={(e) => setChange(e.target.value)} placeholder="Anything else to change (optional)" data-testid="revision-change-input" className="mt-3 h-11 bg-white focus-visible:ring-terracotta" />
      <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes / references (optional)" data-testid="revision-notes-input" className="mt-2 min-h-[70px] bg-white focus-visible:ring-terracotta" />
      {review && (
        <div className="mt-4 rounded-md border border-terracotta/40 bg-sand p-4" data-testid="revision-review">
          <p className="text-xs font-semibold uppercase tracking-wider text-terracotta">Please confirm your revision</p>
          {rows.length === 0 && <p className="mt-2 text-sm text-warmgrey">No field changes — only your note will be sent.</p>}
          <ul className="mt-2 space-y-1 text-sm">{rows.map(([k, a, b]) => <li key={k} data-testid={`revision-diff-${k.split(" ")[0].toLowerCase()}`}><b>{k}:</b> {a} → <span className="font-medium text-charcoal">{b}</span></li>)}</ul>
        </div>
      )}
      <div className="mt-3 flex gap-3">
        {!review ? (
          <Button onClick={() => setReview(true)} disabled={busy || (!rows.length && !change.trim())} data-testid="revision-review-button" className="bg-terracotta text-white hover:bg-terracotta-hover">Review changes</Button>
        ) : (
          <Button onClick={submit} disabled={busy} data-testid="revision-submit" className="bg-terracotta text-white hover:bg-terracotta-hover">Confirm and send revision</Button>
        )}
        <Button variant="ghost" onClick={onCancel} data-testid="revision-cancel">Cancel</Button>
      </div>
    </div>
  );
}
